<template>
  <v-card>
    <v-card-title >{{internalConfig.displayName}}</v-card-title>
    <v-card-text v-if="!selectedYear">
      No data
    </v-card-text>
    <v-card-text v-if="internalConfig.account && selectedYear">
      <v-select
        class="m-0"
        :items="years"
        label="Year"
        outlined
        v-model="selectedYear"
      ></v-select>
      <chart :chartData="sumsForYear" :options="{responsive: true, maintainAspectRatio: true}"/>
    </v-card-text>
    <dashboard-configuration 
      :config="config" 
      :showConfigDialog="showConfigDialog"
      @onConfigureClose="onConfigureClose"
      @onNewConfig="onNewConfig"
      />  
  </v-card>
</template>

<script>
import { Amount, createGrouping } from "@/model";
import Chart from "../common/Chart.vue";
import DashboardConfiguration from './DashboardConfiguration.vue';
import AccountDatesConfig from './AccountDatesConfig.js';

function fillMonths(year, monthEntries) {
  if (monthEntries.length === 12) {
    return monthEntries;
  }
  const entries = [...monthEntries];
  const months = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];
  for(let month of months) {
    const yearMonth = year + "-" + month;
    const index = entries.find(ent => ent[0] === yearMonth);
    if (!index) {
      entries.push([yearMonth, Amount.identity()])
    }
  }
  return entries;
}

export default {
  name: "MonthlyEvolution",
  components: {
    DashboardConfiguration,
    Chart,
  },
  md: 6,
  props: {
    id: {
      type: String,
      default: "",
    },
    showConfigDialog: {
      type: Boolean,
      default: false,
    },
    config: {
      type: Object,
      default: () => {},
    },
  },
  async created() {
    await this.$store.dispatch("getTransactions");
    await this.$store.dispatch("getAccounts");
    this.internalConfig = new AccountDatesConfig(this.config)
  },
  data: () => ({
    internalConfig: new AccountDatesConfig({}),
    selectedYear: undefined
  }),
  computed: {
    evolution() {
      if (!this.internalConfig.account) {
        return [];
      }

      const registerEntries = this.$store.getters["filterRegisterEntries"](
        this.internalConfig.account,
        undefined,
        this.internalConfig.dateRange
      );

      const entriesGroupedByMonth = createGrouping(
        registerEntries.entries,
        (ent) => ent.date.getFullYear().toString() + "-" + (ent.date.getMonth()+1).toString().padStart(2, '0')
      );

      const sumByMonth = [];
      for (const [monthObj, entries] of Object.entries(entriesGroupedByMonth)) {
        const sum = entries.map((ent) => ent.amount).reduce((a1, a2) => a1.add(a2));
        sumByMonth.push([monthObj, sum]);
      }

      const sumGroupedByYear = createGrouping(sumByMonth, tup => tup[0].substr(0,4));
      for (const year of Object.keys(sumGroupedByYear)) {
        let monthEntries = sumGroupedByYear[year];
        monthEntries = fillMonths(year, monthEntries);
        sumGroupedByYear[year] = monthEntries.sort();
      }
      return sumGroupedByYear;
    },
    years() {
      const evolution = this.evolution;
      if (!evolution) {
        return [];
      }
      return Object.keys(evolution);
    },
    sumsForYear() {
      const selectedYear = this.selectedYear || (this.years.length > 0 ? this.years[0] : undefined);
      if ( !selectedYear || !this.evolution || !this.evolution[selectedYear]) {
        return {};
      }
      const sums = this.evolution[selectedYear];
      const labels = [];
      const data = [];
      sums.forEach(tup => {
        labels.push(tup[0]);
        data.push(tup[1].value);
      });

      const df = {
        labels: labels,
        datasets: [{
          label: selectedYear,
          backgroundColor: '#f87979',
          data: data
        }]
      }
      window.df = df;
      return df;
    },    
  },
  methods: {
    onConfigureClose() {
      this.$emit("onConfigureClose", this.id);
    },
    onNewConfig(configJson) {
      this.internalConfig = new AccountDatesConfig(configJson);
      this.$emit("onNewConfig", this.id, configJson);
    }
  },
  watch: {
    years() {
      this.selectedYear = this.years.length > 0 ? this.years[this.years.length-1] : undefined;
    },
  }
};
</script>